import { create } from 'zustand';

export type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'hp_theme_mode';

/**
 * Theme mode is class-driven (not media-query driven). Two classes are applied
 * to <html> when dark is effective:
 *   - `dark`              → consumed by Tailwind (darkMode: 'class') and our
 *                           own SCSS `:root.dark` blocks in global.scss /
 *                           variables.scss
 *   - `ion-palette-dark`  → consumed by @ionic/react/css/palettes/dark.class.css
 *
 * The initial paint is handled by an inline script in index.html, which is
 * the sole authority for resolving mode → isDark on first load. This store
 * reads its initial `isDark` from the class already on <html> rather than
 * recomputing, so the resolution rules live in exactly one place.
 */

function readStoredMode(): ThemeMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {}
  return 'system';
}

function systemPrefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

function resolveIsDark(mode: ThemeMode): boolean {
  return mode === 'dark' || (mode === 'system' && systemPrefersDark());
}

function applyDarkClasses(isDark: boolean) {
  if (typeof document === 'undefined') return;
  const cl = document.documentElement.classList;
  cl.toggle('dark', isDark);
  cl.toggle('ion-palette-dark', isDark);
}

interface ThemeState {
  mode: ThemeMode;
  isDark: boolean;
  setMode: (mode: ThemeMode) => void;
}

const initialMode = readStoredMode();
// Trust the inline init script in index.html — it has already applied the
// correct class to <html> based on the same localStorage key.
const initialIsDark =
  typeof document !== 'undefined' &&
  document.documentElement.classList.contains('dark');

export const useThemeStore = create<ThemeState>((set) => ({
  mode: initialMode,
  isDark: initialIsDark,
  setMode: (mode) => {
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {}
    const isDark = resolveIsDark(mode);
    applyDarkClasses(isDark);
    set({ mode, isDark });
  },
}));

// Subscribe to OS-level color scheme changes. Only takes effect when the
// user's chosen mode is 'system'.
if (typeof window !== 'undefined') {
  const mql = window.matchMedia('(prefers-color-scheme: dark)');
  const handler = (e: MediaQueryListEvent) => {
    if (useThemeStore.getState().mode !== 'system') return;
    applyDarkClasses(e.matches);
    useThemeStore.setState({ isDark: e.matches });
  };
  mql.addEventListener?.('change', handler);
}
